import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { format, isToday } from "date-fns";

import { AttendanceOverviewRowResponse } from "@/api";
import { WEEKDAY_COUNT } from "@/common/components/appointments/shared/appointmentConstants";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { getWeekDays, groupEntriesByDay, localDayKey } from "@/pages/attendance-list/AttendanceList.utils";
import { AttendanceDayCell } from "@/pages/attendance-list/components/table/components/day-cell/AttendanceDayCell";
import { useTranslation } from "@/translations";

interface Props {
  rows: AttendanceOverviewRowResponse[];
  weekStart: Date;
  /** False while the query is unresolved — an empty table then means "not loaded", not "no staff". */
  showEmptyMessage: boolean;
}

const FIXED_COLUMN_COUNT = 2;
const COLUMN_COUNT = FIXED_COLUMN_COUNT + WEEKDAY_COUNT;

export function AttendanceTable({ rows, weekStart, showEmptyMessage }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const weekDays = getWeekDays(weekStart);

  const headerCellSx = { fontWeight: 700, bgcolor: "fsw.blue-5" } as const;

  return (
    // The vertical scroll has to live here, not on a wrapper: stickyHeader pins the header cells to the
    // nearest scrolling ancestor, so with the page scrolling instead the date columns would slide out of
    // view on a list one row per staff member long.
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <Table
        stickyHeader
        size="small"
        aria-label={t("attendanceList.title")}
        sx={{
          tableLayout: "fixed",
          "& .MuiTableBody-root .MuiTableRow-root": { height: 64 },
          // Column dividers are drawn as a left border per cell, so the trailing cell needs a matching
          // right border of its own — otherwise the Friday column is the only one left open on one side.
          // The leading cell opts out explicitly rather than via :first-of-type: the name cell is a <th>
          // row header, which would make the Kürzel cell the first <td> and hand it the exemption instead.
          "& .MuiTableCell-body": {
            py: 1,
            borderLeft: "1px solid",
            borderLeftColor: "fsw.grey-50",
          },
          "& .MuiTableCell-body:last-child": {
            borderRight: "1px solid",
            borderRightColor: "fsw.grey-50",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, width: "18%" }}>{t("attendanceList.columns.name")}</TableCell>
            <TableCell align="center" sx={{ ...headerCellSx, width: "7%" }}>
              {t("attendanceList.columns.userCode")}
            </TableCell>
            {weekDays.map((day) => (
              <TableCell
                key={localDayKey(day)}
                align="center"
                sx={{ ...headerCellSx, ...(isToday(day) && { bgcolor: "fsw.blue-25" }) }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {format(day, "EEEEEE", { locale: locales[language] })}
                </Typography>
                <Typography variant="body2">{format(day, "dd.MM.", { locale: locales[language] })}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && showEmptyMessage && (
            <TableRow>
              {/* Spans every column, so it is both the leading and the trailing cell — it takes neither
                  the divider nor the closing right border. */}
              <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ borderLeft: "none", borderRight: "none" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("attendanceList.empty")}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => {
            const entriesByDay = groupEntriesByDay(row);
            return (
              <TableRow key={row.counselorId}>
                {/* Row header, not a plain cell: the name is what identifies the row, so a screen reader
                    has to announce it alongside the day column when reading a cell. No explicit
                    scope="row" — every other cell in the row is a <td>, which is exactly the case the
                    HTML header-assignment algorithm resolves to a row header on its own. */}
                <TableCell component="th" sx={{ fontWeight: 700, borderLeft: "none" }}>
                  {row.lastName} {row.firstName}
                </TableCell>
                <TableCell align="center">{row.userCode}</TableCell>
                {weekDays.map((day) => (
                  <AttendanceDayCell key={localDayKey(day)} entries={entriesByDay.get(localDayKey(day)) ?? []} />
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
